import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

/**
 * CatalogItemPicker — reusable line-item component backed by the Products/Services catalog.
 * Used in Deals, Quotes, and Invoices.
 *
 * Props:
 *  - lineItems, setLineItems, currency, readOnly
 */

const EMPTY_ITEM = () => ({
  _key: Date.now() + Math.random(),
  catalog_item_id: null,
  description: '',
  quantity: 1,
  price: 0,
  taxRate: 0,
  unit: 'each',
  billing_type: 'one_time',
  hsn_sac: '',
});

export { EMPTY_ITEM };

export default function CatalogItemPicker({ lineItems, setLineItems, currency, readOnly }) {
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchIdx, setActiveSearchIdx] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => { loadCatalogItems(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setActiveSearchIdx(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadCatalogItems = async () => {
    try {
      setCatalogLoading(true);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = res.data;
      setCatalogItems(Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : []);
    } catch { setCatalogItems([]); }
    finally { setCatalogLoading(false); }
  };

  const updateItem = (idx, field, value) => {
    setLineItems(prev => prev.map((item, i) => i !== idx ? item : { ...item, [field]: value }));
  };

  const selectCatalogItem = (idx, cat) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return {
        ...item,
        catalog_item_id: cat.id,
        description: cat.name + (cat.description ? ` — ${cat.description}` : ''),
        price: cat.unit_price || cat.selling_price || 0,
        taxRate: cat.tax_rate || 0,
        unit: cat.unit || 'each',
        billing_type: cat.billing_type || 'one_time',
        hsn_sac: cat.hsn_sac || '',
      };
    }));
    setActiveSearchIdx(null);
    setSearchQuery('');
  };

  const clearCatalogLink = (idx) => {
    updateItem(idx, 'catalog_item_id', null);
  };

  const addItem = () => setLineItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (idx) => { if (lineItems.length > 1) setLineItems(prev => prev.filter((_, i) => i !== idx)); };

  const filteredCatalog = catalogItems.filter(c =>
    !searchQuery ||
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const calcSubtotal = () => lineItems.reduce((s, it) => s + (it.quantity || 0) * (it.price || 0), 0);
  const calcTax = () => lineItems.reduce((s, it) => s + ((it.quantity || 0) * (it.price || 0)) * ((it.taxRate || 0) / 100), 0);

  const fmt = (val) => currency?.format ? currency.format(val) : `$${Number(val).toFixed(2)}`;

  return (
    <div className="space-y-3" data-testid="catalog-item-picker" ref={pickerRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <Package className="h-4 w-4" />Line Items
          {catalogLoading && <span className="text-xs text-muted-foreground animate-pulse">(loading catalog...)</span>}
        </h4>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <Button
                variant="ghost" size="sm" className="text-xs h-7 text-muted-foreground hover:text-primary"
                onClick={() => window.open('/settings/products', '_blank')}
                data-testid="manage-catalog-btn"
              >
                <ExternalLink className="h-3 w-3 mr-1" />Manage Catalog
              </Button>
              <Button variant="outline" size="sm" className="h-7" onClick={addItem} data-testid="add-line-item-btn">
                <Plus className="h-3 w-3 mr-1" />Add Row
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div className="space-y-2">
        {lineItems.map((item, idx) => (
          <div key={item._key || idx} className="rounded-lg border bg-card/50 p-3 space-y-2.5 transition-shadow hover:shadow-sm" data-testid={`line-item-${idx}`}>
            {/* Search / Description Row */}
            <div className="relative">
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder={readOnly ? '' : 'Search products & services or type custom...'}
                    value={activeSearchIdx === idx ? searchQuery : item.description}
                    onChange={e => {
                      if (readOnly) return;
                      setActiveSearchIdx(idx);
                      setSearchQuery(e.target.value);
                      updateItem(idx, 'description', e.target.value);
                      if (item.catalog_item_id) clearCatalogLink(idx);
                    }}
                    onFocus={() => { if (!readOnly) { setActiveSearchIdx(idx); setSearchQuery(item.description || ''); } }}
                    readOnly={readOnly}
                    className="pl-8 text-sm h-9"
                    data-testid={`line-item-desc-${idx}`}
                  />
                  {item.catalog_item_id && (
                    <Badge variant="secondary" className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] bg-emerald-500/15 text-emerald-400 border-0">
                      From Catalog
                    </Badge>
                  )}
                </div>

                {/* + New Item (opens catalog in new tab) */}
                {!readOnly && (
                  <Button
                    variant="outline" size="icon" className="h-9 w-9 shrink-0"
                    title="Create new catalog item"
                    onClick={() => window.open('/settings/products', '_blank')}
                    data-testid={`new-catalog-item-${idx}`}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}

                {/* Remove */}
                {!readOnly && lineItems.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-red-400 shrink-0" onClick={() => removeItem(idx)} data-testid={`remove-line-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              {/* Catalog Dropdown */}
              {activeSearchIdx === idx && !readOnly && filteredCatalog.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-12 mt-1 max-h-52 overflow-y-auto rounded-md border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95" data-testid={`catalog-dropdown-${idx}`}>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b bg-muted/30">
                    Select from catalog ({filteredCatalog.length})
                  </div>
                  {filteredCatalog.map(cat => (
                    <button key={cat.id}
                      className="w-full text-left px-3 py-2.5 hover:bg-muted/50 flex items-center justify-between text-sm transition-colors border-b border-border/30 last:border-0"
                      onClick={() => selectCatalogItem(idx, cat)}
                      data-testid={`catalog-option-${cat.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{cat.name}</span>
                          <Badge variant="outline" className="text-[8px] shrink-0">{cat.item_type === 'product' ? 'Product' : 'Service'}</Badge>
                        </div>
                        {cat.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{cat.description}</p>}
                        {cat.sku && <p className="text-[10px] text-muted-foreground/60">SKU: {cat.sku}</p>}
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <span className="font-mono text-sm font-medium">{fmt(cat.unit_price)}</span>
                        <p className="text-[10px] text-muted-foreground">{cat.tax_rate ? `${cat.tax_rate}% tax` : 'No tax'} · {cat.billing_type === 'recurring' ? 'Recurring' : 'One-time'}</p>
                      </div>
                    </button>
                  ))}
                  {/* Quick link at bottom */}
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-primary hover:bg-primary/5 flex items-center gap-1.5 border-t font-medium"
                    onClick={() => window.open('/settings/products', '_blank')}
                    data-testid={`catalog-create-new-${idx}`}
                  >
                    <Plus className="h-3 w-3" /> Create new product/service
                  </button>
                </div>
              )}

              {/* Empty state — no results */}
              {activeSearchIdx === idx && !readOnly && searchQuery && filteredCatalog.length === 0 && !catalogLoading && (
                <div className="absolute z-50 top-full left-0 right-12 mt-1 rounded-md border bg-popover shadow-lg p-4 text-center" data-testid={`catalog-empty-${idx}`}>
                  <p className="text-sm text-muted-foreground mb-2">No catalog items match "{searchQuery}"</p>
                  <Button variant="outline" size="sm" onClick={() => window.open('/settings/products', '_blank')} data-testid={`catalog-create-from-empty-${idx}`}>
                    <Plus className="h-3 w-3 mr-1" /> Create New Item
                  </Button>
                </div>
              )}
            </div>

            {/* Qty / Price / Tax / HSN / Subtotal Row */}
            <div className="grid grid-cols-5 gap-2">
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Quantity</label>
                <Input type="number" min="0" step="1" value={item.quantity}
                  onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                  readOnly={readOnly} className="text-sm h-8" data-testid={`line-item-qty-${idx}`} />
              </div>
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Unit Price</label>
                <Input type="number" min="0" step="0.01" value={item.price}
                  onChange={e => updateItem(idx, 'price', parseFloat(e.target.value) || 0)}
                  readOnly={readOnly} className="text-sm h-8" data-testid={`line-item-price-${idx}`} />
              </div>
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Tax %</label>
                <Input type="number" min="0" max="100" step="0.1" value={item.taxRate}
                  onChange={e => updateItem(idx, 'taxRate', parseFloat(e.target.value) || 0)}
                  readOnly={readOnly} className="text-sm h-8" data-testid={`line-item-tax-${idx}`} />
              </div>
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">HSN/SAC</label>
                <Input type="text" value={item.hsn_sac || ''}
                  onChange={e => updateItem(idx, 'hsn_sac', e.target.value)}
                  readOnly={readOnly} placeholder="—" className="text-sm h-8" data-testid={`line-item-hsn-${idx}`} />
              </div>
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Subtotal</label>
                <div className="h-8 flex items-center justify-end px-2 text-sm font-mono bg-muted/30 rounded-md border border-transparent" data-testid={`line-item-subtotal-${idx}`}>
                  {fmt((item.quantity || 0) * (item.price || 0))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-56 space-y-1 text-sm">
          <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span data-testid="picker-subtotal">{fmt(calcSubtotal())}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>Tax</span><span data-testid="picker-tax">{fmt(calcTax())}</span></div>
          <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span data-testid="picker-total">{fmt(calcSubtotal() + calcTax())}</span></div>
        </div>
      </div>
    </div>
  );
}
